import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { noticeService, NoticeRecord } from '../lib/api/notices';

interface NoticeStoreState {
  /** All active notices for the current board. */
  notices: NoticeRecord[];

  /** The boardId currently hydrated for. */
  activeBoardId: string | null;

  /** Whether initial hydration from server has completed. */
  isHydrated: boolean;

  // ─── Actions ─────────────────────────────────────────────

  /**
   * Fetch all notices from the server for a given board.
   * Called once on board mount.
   */
  hydrateFromServer: (boardId: string) => Promise<void>;

  /**
   * Create or update a notice via the backend.
   * Optimistically updates local state.
   */
  upsertNotice: (boardId: string, data: { refKey: string; title: string; state?: string; loading?: boolean; sticky?: boolean; payload?: Record<string, any> }) => Promise<void>;

  /**
   * Dismiss a notice — remove from local state + call backend.
   * Only works for non-sticky notices.
   */
  dismissNotice: (boardId: string, refKey: string) => Promise<void>;

  /**
   * Apply a remote upsert from WebSocket (no network call).
   */
  applyRemoteUpsert: (notice: NoticeRecord) => void;

  /**
   * Apply a remote dismissal from WebSocket (no network call).
   */
  applyRemoteDismissal: (refKey: string) => void;

  /**
   * Clear all notices (called on board unmount or board switch).
   */
  clearAll: () => void;
}

export const useNoticeStore = create<NoticeStoreState>()(
  devtools(
    (set, get) => ({
      notices: [],
      activeBoardId: null,
      isHydrated: false,

      hydrateFromServer: async (boardId: string) => {
        try {
          const notices = await noticeService.fetchForBoard(boardId);
          set({ notices, activeBoardId: boardId, isHydrated: true });
        } catch (error) {
          console.error('[NoticeStore] Hydration failed:', error);
          // Still mark as hydrated to avoid infinite retry loops
          set({ notices: [], activeBoardId: boardId, isHydrated: true });
        }
      },

      upsertNotice: async (boardId: string, data: { refKey: string; title: string; state?: string; loading?: boolean; sticky?: boolean; payload?: Record<string, any> }) => {
        // Optimistic update
        const optimistic: NoticeRecord = {
            id: 'temp-' + Date.now(),
            boardId,
            refKey: data.refKey,
            title: data.title,
            state: data.state || '',
            loading: data.loading || false,
            sticky: data.sticky ?? true,
            payload: data.payload || {},
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        };
        get().applyRemoteUpsert(optimistic);

        try {
            await noticeService.upsert(boardId, data);
        } catch (error) {
            console.error('[NoticeStore] Upsert failed:', error);
            get().hydrateFromServer(boardId);
        }
      },

      dismissNotice: async (boardId: string, refKey: string) => {
        // Optimistically remove from local state
        set(state => ({
          notices: state.notices.filter(n => n.refKey !== refKey),
        }));

        try {
          await noticeService.dismiss(boardId, refKey);
        } catch (error) {
          console.error('[NoticeStore] Dismiss failed:', error);
          // Re-hydrate from server to restore correct state
          get().hydrateFromServer(boardId);
        }
      },

      applyRemoteUpsert: (notice: NoticeRecord) => {
        set(state => {
          const existingIdx = state.notices.findIndex(n => n.refKey === notice.refKey);
          if (existingIdx >= 0) {
            // Update existing notice in place
            const updated = [...state.notices];
            updated[existingIdx] = notice;
            return { notices: updated };
          }
          // Prepend new notice
          return { notices: [notice, ...state.notices] };
        });
      },

      applyRemoteDismissal: (refKey: string) => {
        set(state => ({
          notices: state.notices.filter(n => n.refKey !== refKey),
        }));
      },

      clearAll: () => {
        set({ notices: [], activeBoardId: null, isHydrated: false });
      },
    }),
    { name: 'NoticeStore' },
  ),
);
